import { useState } from 'react';
import { useForm } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import moment from 'moment';
import { Check, PageNotFound } from '@lendi-ui/fancy-icon';
import { Body, Heading } from '@lendi-ui/typography';

import { EMPLOYMENT_TYPE, FIELD_PATHS, URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { useCustomer, useFormValues } from '@funnels/hooks';
import { ApplicantEntityIndex, StoreEmployment, UseParams } from '@funnels/types';
import {
  AccordionGroup,
  ConfirmDialog,
  FunnelGrid,
  FunnelGridUnit,
  IconContainer,
} from '@funnels/ui-components/Common';
import { CompanyMerger } from '@funnels/ui-components/RelatedCompany';
import { Card } from '@funnels/ui-layouts/customer-view/_common';
import { getDashboardUrl, initialFormValues } from '@funnels/utils';

import { EmploymentAccordion } from '../../Layout/EmploymentAccordion';

interface EmploymentHistoryProps extends ApplicantEntityIndex {
  isFromSummary?: boolean;
}

const isMoreThan3Years = (date?: string) => {
  const minYear = moment().startOf('year').subtract(115, 'years');
  const parsedDate = moment(date, 'YYYY-MM-DD', true);
  return parsedDate.isValid() && parsedDate > minYear && moment().diff(moment(date), 'years') >= 3;
};

const renderConfirmModalBody = (hasMoreThan3YearsHistory: boolean) =>
  hasMoreThan3YearsHistory ? (
    <>
      <IconContainer mt="xl">
        <Check width="72px" height="72px" />
      </IconContainer>
      <Heading size="sm" mb="md" mt="sm">
        Success!
      </Heading>
      <Body mt="xxs">You have provided 3 years of employment history</Body>
    </>
  ) : (
    <>
      <IconContainer mt="xl">
        <PageNotFound width="72px" height="72px" />
      </IconContainer>
      <Heading size="sm" mb="md" mt="sm">
        Something's missing
      </Heading>
      <Body mt="xxs">You need to provide details for a full 3 years of employment history</Body>
    </>
  );

export const EmploymentHistory = ({ applicantIndex, isFromSummary = false }: EmploymentHistoryProps) => {
  const form = useForm();
  const formState = form.getState();
  const [formValues, setFormValue] = useFormValues();
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = useParams<UseParams>();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const applicantEmployments = employments.filter(
    (employment: StoreEmployment) =>
      get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicantId
  );

  const hasMoreThan3YearsHistory = applicantEmployments.some((employment) =>
    isMoreThan3Years(get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_START_DATE(0)))
  );

  const handleConfirmModal = () => {
    setIsModalVisible(false);
    if (hasMoreThan3YearsHistory && applicationId) {
      window.location.assign(getDashboardUrl(applicationId));
    }
  };

  const handleDoneClick = () => {
    if (formState.valid) {
      setIsModalVisible(true);
    }
  };

  const handleAddEmployment = () => {
    const employments = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);

    const type = EMPLOYMENT_TYPE.PREVIOUS;
    const newEmployment = initialFormValues.INITIAL_EMPLOYMENT_V2(applicantId, type);

    setFormValue(FIELD_PATHS.EMPLOYMENTS(), [...employments, newEmployment]);
  };

  const customer = useCustomer();
  const applicantCustomerId = get(formValues, FIELD_PATHS.APPLICANT_CUSTOMER_ID(applicantIndex));
  const applicantFirstName = get(formValues, FIELD_PATHS.APPLICANT_FIRST_NAME(applicantIndex));
  const isLoggedInApplicant = applicantCustomerId === customer.data.id;
  const heading = `Enter ${isLoggedInApplicant ? 'your' : `${applicantFirstName}'s`} employment history`;

  return (
    <Card
      heading={heading}
      actionCustomText={isFromSummary ? 'Save' : 'Next'}
      onActionClick={isFromSummary ? undefined : handleDoneClick}
    >
      <FunnelGrid>
        <CompanyMerger />
        <FunnelGridUnit>
          <AccordionGroup addButtonText="Add Previous Role" onAdd={handleAddEmployment}>
            {employments.map((_, index) => {
              if (get({ employments }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(index)) === applicantId) {
                return (
                  <EmploymentAccordion
                    employmentIndex={index}
                    applicantId={applicantId}
                    key={get({ employments }, FIELD_PATHS.EMPLOYMENT_CLIENT_ID(index))}
                  />
                );
              }
              return null;
            })}
          </AccordionGroup>
        </FunnelGridUnit>
      </FunnelGrid>
      <ConfirmDialog
        isVisible={isModalVisible}
        body={renderConfirmModalBody(hasMoreThan3YearsHistory)}
        onCancel={() => setIsModalVisible(false)}
        onConfirm={() => handleConfirmModal()}
        confirmButtonText={hasMoreThan3YearsHistory ? 'Return to dashboard' : 'ok'}
        centeredContent
        shouldHideCancelButton
      />
    </Card>
  );
};
